"use client";
import useMediaQuery from "@repo/shared-ui/useMediaQuery";
import React, { useRef, useState } from "react";
import "swiper/css";
import "swiper/css/navigation";
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y, Navigation, Pagination } from "swiper/modules";
import StockCard from "./StockInfoCard";
import { StocksGridProps } from "./types";
import StockListNotFound from "./StockListNotFound";
import StockCardSkeleton from "./StockCardSkeleton";
import SwiperButton from "@repo/shared-ui/SwiperButton";
import { dlElementConstants, dlEventConstants } from "@repo/analytics/data-layer.constants";
import { dataLayer } from "@repo/analytics/data-layer-utils";

const SKELETON_CARD_COUNT = 6;

const StockGridSkeletonRow = () => (
  <div className="relative flex overflow-hidden">
    {Array.from({ length: SKELETON_CARD_COUNT }).map((_, index) => (
      <div
        key={"StockGridSkeleton-" + index}
        className={`min-w-[156px] max-w-[156px] md:min-w-[214px] md:max-w-[214px] ${
          index === SKELETON_CARD_COUNT - 1 ? "mr-1" : "mr-3"
        }`}
      >
        <StockCardSkeleton />
      </div>
    ))}
  </div>
);

export const StocksGrid: React.FC<StocksGridProps> = ({
  stocks,
  defaultImage,
  errorMessage,
  stockNotFoundImgUrl,
  modalTitle,
  modalBtnCta,
  successMsg,
  errorMsg,
  onCardClick,
  section_title,
  cta_text,
  nextArrowClassName,
  isLoading = false,
  hidePriceChange = false,
  isCardClickable = true,
  disableDefaultRedirection = false,
}) => {
  const isMobile = useMediaQuery("(max-width: 600px)");

  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const [end, setEnd] = useState(false);
  const [start, setStart] = useState(true);

  const handleNext = () => {
    const data = {
      section_title: section_title,
      component: section_title,
      event: dlEventConstants.CLICK,
      element: dlElementConstants.VIEW_ALL_CLICK,
      cta_text: cta_text,
    };
    const cleverTapObj = {
      EP_PAGE_NAME: dlElementConstants.TRADING_MALL_CLP,
      EP_JOURNEY_NAME: dlElementConstants.TRADING_MALL_CLP_EP,
    };
    dataLayer("", data, cleverTapObj);
  };

  const handlePrev = () => {
    const dataLayerObj = {
      section_title: section_title,
      component: section_title,
      event: dlEventConstants.CLICK,
      element: dlElementConstants.VIEW_ALL_CLICK,
      cta_text: cta_text,
    };
    const cleverTapObject = {
      EP_PAGE_NAME: dlElementConstants.TRADING_MALL_CLP,
      EP_JOURNEY_NAME: dlElementConstants.TRADING_MALL_CLP_EP,
    };
    dataLayer("", dataLayerObj, cleverTapObject);
  };

  const showSkeleton = isLoading;

  let content;
  if (showSkeleton) {
    content = <StockGridSkeletonRow />;
  } else if (!stocks || stocks?.length <= 0) {
    content = (
      <StockListNotFound
        errorMessage={errorMessage || ""}
        stockNotFoundImgUrl={stockNotFoundImgUrl || ""}
      />
    );
  } else {
    content = (
      <div className="relative">
        {isMobile ? null : (
          <>
            <SwiperButton ref={prevRef} direction="prev" isDisabled={start} onClick={handlePrev} />
            <SwiperButton ref={nextRef} direction="next" isDisabled={end} onClick={handleNext} className={nextArrowClassName} />
          </>
        )}

        <Swiper
          modules={[Navigation, Pagination, A11y]}
          spaceBetween={0}
          slidesPerView="auto"
          allowTouchMove={true}
          observer={true}
          observeParents={true}
          navigation={isMobile ? false : {
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          onBeforeInit={(swiper: any) => {
            if (isMobile) return;
            if (typeof swiper.params.navigation !== "boolean") {
              swiper.params.navigation.prevEl = prevRef.current;
              swiper.params.navigation.nextEl = nextRef.current;
            }
          }}
          onInit={(swiper: any) => {
            setStart(swiper.isBeginning);
            setEnd(swiper.isEnd);
          }}
          onSlideChange={(swiper: any) => {
            setStart(swiper.isBeginning);
            setEnd(swiper.isEnd);
          }}
          onReachEnd={() => setEnd(true)}
          onFromEdge={() => setEnd(false)}
        >
          {stocks.map((stock, index: number) => (
            <SwiperSlide
              key={"StockGrid-" + index}
              className={`min-w-[156px]! max-w-[156px] md:min-w-[214px]! md:max-w-[214px] ${index === stocks.length - 1 ? "mr-1!" : "mr-3!"
                }`}
            >
              <StockCard
                stock={stock}
                defaultImage={defaultImage}
                modalTitle={modalTitle}
                modalBtnCta={modalBtnCta}
                successMsg={successMsg}
                errorMsg={errorMsg}
                onCardClick={onCardClick}
                cta_text={cta_text}
                section_title={section_title}
                hidePriceChange={hidePriceChange}
                isCardClickable={isCardClickable}
                disableDefaultRedirection={disableDefaultRedirection}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    );
  }

  return <div className="relative">{content}</div>;
};
